/**
 * MultiBrandDashboard — Iter Feb 2026 R42 · Phase 3.
 *
 * Side-by-side matrix: rows = brands, columns = KPIs (Spend, Impr,
 * Clicks, CTR, CPC, Leads, CPL, Revenue, ROAS + vs-prev deltas).
 *
 * Adds a client-rollup card grid on top so you can see each client's
 * aggregated performance and click through to the branded PDF report.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { API as API_BASE } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LayoutGrid, RefreshCw, Download, TrendingUp, TrendingDown, Users } from "lucide-react";

const fmtMoney = (v) => typeof v === "number" ? `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 0 })}` : "—";
const fmtInt = (v) => typeof v === "number" ? v.toLocaleString("en-IN") : "—";
const fmtPct = (v) => typeof v === "number" ? `${v.toFixed(2)}%` : "—";

function Delta({ v }) {
  if (v == null) return <span className="text-muted-foreground text-[10px]">—</span>;
  const up = v >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`text-[10px] font-mono ${up ? "text-emerald-600" : "text-rose-600"} inline-flex items-center gap-0.5`}>
      <Icon className="w-2.5 h-2.5" /> {up ? "+" : ""}{v}%
    </span>
  );
}

export default function MultiBrandDashboard() {
  const { activeWorkspaceId } = useAuth();
  const today = new Date().toISOString().slice(0, 10);
  const thirtyAgo = new Date(Date.now() - 30 * 86400_000).toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(thirtyAgo);
  const [dateTo, setDateTo] = useState(today);
  const [data, setData] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const { data: d } = await api.get(`/workspaces/${activeWorkspaceId}/perf/multi-brand`, {
        params: { date_from: dateFrom, date_to: dateTo },
      });
      setData(d);
      // Group brands by client so we can render a rollup strip
      const cmap = {};
      for (const r of d.rows || []) {
        const cid = r.client_id || "__no_client__";
        cmap[cid] = cmap[cid] || { client_id: r.client_id, spend: 0, leads: 0, revenue: 0, brand_count: 0 };
        cmap[cid].spend += r.spend || 0;
        cmap[cid].leads += r.leads || 0;
        cmap[cid].revenue += r.revenue || 0;
        cmap[cid].brand_count++;
      }
      setClients(Object.values(cmap).filter(c => c.client_id));
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load matrix")); }
    finally { setLoading(false); }
  }, [activeWorkspaceId, dateFrom, dateTo]);
  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  const nonEmpty = useMemo(() => rows.filter(r => !r.empty), [rows]);

  const downloadClientPdf = (cid) => {
    const token = localStorage.getItem("co_token");
    const url = `${API_BASE}/workspaces/${activeWorkspaceId}/perf/clients/${cid}/pdf-report?date_from=${dateFrom}&date_to=${dateTo}`;
    // Fetch with auth header and trigger download
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u; a.download = `client-${cid}-report.pdf`; a.click();
        URL.revokeObjectURL(u);
      })
      .catch(e => toast.error("PDF download failed"));
  };

  return (
    <div className="flex flex-col h-full" data-testid="multi-brand-dashboard-page">
      <PageHeader
        eyebrow="Performance"
        title="Multi-brand Dashboard"
        description="Side-by-side comparison across every brand: spend, engagement, leads, revenue, ROAS. Filter by date range; download branded PDFs per client."
        actions={
          <div className="flex items-center gap-2">
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-9 rounded-none text-xs w-36" data-testid="mb-date-from" />
            <span className="text-muted-foreground">→</span>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-9 rounded-none text-xs w-36" data-testid="mb-date-to" />
            <Button variant="outline" className="rounded-none h-9" onClick={load}>
              <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
            </Button>
          </div>
        }
      />
      <div className="flex-1 overflow-auto px-4 sm:px-6 md:px-10 py-4 space-y-4">

        {/* Totals strip */}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="mb-totals-strip">
            {[
              ["Spend",   fmtMoney(data.totals.spend)],
              ["Leads",   fmtInt(data.totals.leads)],
              ["CPL",     fmtMoney(data.totals.cpl)],
              ["Revenue", fmtMoney(data.totals.revenue)],
              ["ROAS",    typeof data.totals.roas === "number" ? `${data.totals.roas}×` : "—"],
            ].map(([label, val]) => (
              <div key={label} className="border p-3">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
                <div className="text-xl font-semibold font-mono">{val}</div>
              </div>
            ))}
          </div>
        )}

        {/* Client PDF strip */}
        {clients.length > 0 && (
          <div className="border">
            <div className="px-3 py-2 border-b bg-neutral-50 text-[10px] uppercase tracking-widest text-muted-foreground">
              Client roll-up — download branded PDF
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 p-2">
              {clients.map(c => (
                <div key={c.client_id} className="border p-2 flex items-center gap-3" data-testid={`mb-client-${c.client_id}`}>
                  <Users className="w-4 h-4 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">Client · {c.client_id.slice(0, 8)}</div>
                    <div className="text-xs text-muted-foreground">
                      {c.brand_count} brand{c.brand_count === 1 ? "" : "s"} · {fmtMoney(c.spend)} spend · {fmtInt(c.leads)} leads
                    </div>
                  </div>
                  <Link to={`/app/performance/clients/${c.client_id}`}>
                    <Button size="sm" variant="outline" className="rounded-none h-8">Open</Button>
                  </Link>
                  <Button size="sm" className="rounded-none h-8" onClick={() => downloadClientPdf(c.client_id)} data-testid={`mb-pdf-${c.client_id}`}>
                    <Download className="w-3 h-3 mr-1" /> PDF
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Matrix */}
        <div className="border overflow-hidden">
          <div className="px-3 py-2 border-b bg-neutral-50 flex items-center gap-2">
            <LayoutGrid className="w-4 h-4" />
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Brand matrix · {data?.date_from} → {data?.date_to} · {nonEmpty.length}/{rows.length} brands with activity</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="mb-matrix-table">
              <thead className="text-[10px] uppercase tracking-widest text-muted-foreground bg-neutral-50">
                <tr className="border-b">
                  <th className="text-left px-2 py-1.5">Brand</th>
                  <th className="text-right px-2 py-1.5">Spend</th>
                  <th className="text-right px-2 py-1.5">Impr</th>
                  <th className="text-right px-2 py-1.5">Clicks</th>
                  <th className="text-right px-2 py-1.5">CTR</th>
                  <th className="text-right px-2 py-1.5">CPC</th>
                  <th className="text-right px-2 py-1.5">Leads</th>
                  <th className="text-right px-2 py-1.5">CPL</th>
                  <th className="text-right px-2 py-1.5">Revenue</th>
                  <th className="text-right px-2 py-1.5">ROAS</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={10} className="py-3 text-center text-muted-foreground">Loading…</td></tr>}
                {!loading && nonEmpty.length === 0 && (
                  <tr><td colSpan={10} className="py-6 text-center text-muted-foreground">
                    No brand activity in this range. Ensure ad accounts are linked to brands (Performance → Accounts).
                  </td></tr>
                )}
                {nonEmpty.map(r => (
                  <tr key={r.brand_id} className="border-b hover:bg-neutral-50" data-testid={`mb-row-${r.brand_id}`}>
                    <td className="px-2 py-1.5 font-medium truncate max-w-[240px]" title={r.brand_name}>
                      <Link to={`/app/brands/${r.brand_id}`} className="hover:underline">{r.brand_name}</Link>
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">
                      {fmtMoney(r.spend)} <Delta v={r.delta_spend_pct} />
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">{fmtInt(r.impressions)}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{fmtInt(r.clicks)}</td>
                    <td className="px-2 py-1.5 text-right font-mono">
                      {fmtPct(r.ctr)} <Delta v={r.delta_ctr_pct} />
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">{fmtMoney(r.cpc)}</td>
                    <td className="px-2 py-1.5 text-right font-mono">
                      {fmtInt(r.leads)} <Delta v={r.delta_leads_pct} />
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">{fmtMoney(r.cpl)}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{fmtMoney(r.revenue)}</td>
                    <td className="px-2 py-1.5 text-right font-mono font-semibold">
                      {typeof r.roas === "number" && r.roas > 0 ? `${r.roas}×` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
